import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import CONFIG from '../../config';
import './FieldMonitorDashboard.css';
import Profile from '../AdminDashboard/Profile';
import FieldMonitorSidebar from './FieldMonitorSidebar';
import FieldMonitorNavbar from './FieldMonitorNavbar';
import LoadingSpinner from '../common/LoadingSpinner';
import { SimulationProvider, useSimulation } from '../../contexts/SimulationContext';

const FieldMonitorContent = ({ onLogout }) => {
    const { project, setProject, candidates, loading, addAlert, simulateArrivals, moveCandidates, triggerFailure } = useSimulation();
    const [activeTab, setActiveTab] = useState('incident-scan');
    const [scanId, setScanId] = useState('');
    const [activeCandidate, setActiveCandidate] = useState(null);
    const [pendingSyncs, setPendingSyncs] = useState(0);
    const [recentLogs, setRecentLogs] = useState([]);
    const [incidentHistory, setIncidentHistory] = useState([]);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [historySearch, setHistorySearch] = useState('');
    const [historyTypeFilter, setHistoryTypeFilter] = useState('ALL');
    const [selectedIncident, setSelectedIncident] = useState(null);
    const [isSimOpen, setIsSimOpen] = useState(false);
    const [selectedCandidateForDrawer, setSelectedCandidateForDrawer] = useState(null);
    const [queueSearch, setQueueSearch] = useState('');

    const scanInputRef = useRef(null);

    useEffect(() => {
        const storedLogs = JSON.parse(localStorage.getItem('fm_recent_logs') || '[]');
        setRecentLogs(storedLogs);

        const storedPending = JSON.parse(localStorage.getItem('fm_pending_syncs') || '[]');
        setPendingSyncs(storedPending.length);

        const handleOnline = () => {
            setIsOffline(false);
            processPendingSyncs();
        };
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        if (navigator.onLine) processPendingSyncs();

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        if (activeTab === 'history' && project) {
            fetchIncidentHistory();
        }
    }, [activeTab, project]);

    const fetchIncidentHistory = async () => {
        if (!project) return;
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${CONFIG.API_URL}/api/incidents/${project._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIncidentHistory(res.data);
        } catch (err) {
            console.error('Failed to fetch history:', err);
        }
    };

    useEffect(() => {
        if (activeTab === 'incident-scan' && scanInputRef.current) {
            scanInputRef.current.focus();
        }
    }, [activeTab]);

    const processPendingSyncs = async () => {
        const pending = JSON.parse(localStorage.getItem('fm_pending_syncs') || '[]');
        if (pending.length === 0) return;

        const token = localStorage.getItem('token');
        const remaining = [];

        for (const action of pending) {
            try {
                // Normalize legacy types if any (e.g. INJURED -> INJURY)
                if (action.type === 'INJURED') action.type = 'INJURY';

                await axios.post(`${CONFIG.API_URL}/api/incidents`, action, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (err) {
                // If it's a validation error (400), don't keep it in remaining as it's poison data
                if (err.response?.status === 400) {
                    console.error('Poison sync data rejected:', action);
                } else {
                    remaining.push(action);
                }
            }
        }

        localStorage.setItem('fm_pending_syncs', JSON.stringify(remaining));
        setPendingSyncs(remaining.length);
        if (remaining.length === 0) {
            addAlert('All pending incidents have been synced successfully.', 'success');
            fetchIncidentHistory();
            setProject(project, true); // Refresh candidates too
        } else if (remaining.length < pending.length) {
            addAlert(`Synced ${pending.length - remaining.length} incidents. ${remaining.length} still pending.`, 'info');
        }
    };

    const handleScan = (e) => {
        if (e) e.preventDefault();
        const cand = candidates.find(c =>
            c.chest_number === scanId ||
            c.unique_id === scanId ||
            c._id === scanId
        );

        if (cand) {
            if (cand.current_stage !== 'TRACKER') {
                Swal.fire({
                    icon: 'warning',
                    title: 'Wrong Stage',
                    text: `Candidate ${cand.full_name} is currently at ${cand.current_stage}. Incidents can only be reported for active runners.`,
                    confirmButtonColor: 'var(--primary)',
                    customClass: { popup: 'premium-swal' }
                });
                setScanId('');
                return;
            }
            if (cand.status !== 'IN_PROGRESS') {
                Swal.fire({
                    icon: 'error',
                    title: 'Inactive Candidate',
                    text: `Candidate ${cand.full_name} is not currently running (Status: ${cand.status}).`,
                    confirmButtonColor: 'var(--primary)',
                    customClass: { popup: 'premium-swal' }
                });
                setScanId('');
                return;
            }
            setActiveCandidate(cand);
            setScanId('');
        } else {
            addAlert(`Candidate ID "${scanId}" not found.`, 'error');
            setScanId('');
        }
    };

    const reportIncident = async (type) => {
        if (!activeCandidate || !project) return;

        // Add proper loading indication via SweetAlert
        Swal.fire({
            title: 'Processing Action...',
            text: `Updating ${activeCandidate.full_name}'s mission status.`,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            },
            customClass: { popup: 'premium-swal' }
        });

        const action = {
            candidateId: activeCandidate._id,
            projectId: project._id,
            type,
            timestamp: new Date().toISOString(),
            chestNumber: activeCandidate.chest_number,
            fullName: activeCandidate.full_name
        };

        const newLog = {
            id: Date.now(),
            ...action
        };

        const updatedLogs = [newLog, ...recentLogs].slice(0, 5);
        setRecentLogs(updatedLogs);
        localStorage.setItem('fm_recent_logs', JSON.stringify(updatedLogs));

        if (isOffline) {
            const pending = JSON.parse(localStorage.getItem('fm_pending_syncs') || '[]');
            localStorage.setItem('fm_pending_syncs', JSON.stringify([...pending, action]));
            setPendingSyncs(pending.length + 1);
            Swal.fire({
                title: 'Incident Saved',
                text: 'Data saved locally for offline synchronization.',
                icon: 'info',
                confirmButtonColor: 'var(--primary)',
                customClass: { popup: 'premium-swal' }
            });
            setActiveCandidate(null);
        } else {
            try {
                const token = localStorage.getItem('token');
                await axios.post(`${CONFIG.API_URL}/api/incidents`, action, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                // Close loading and show success
                Swal.fire({
                    title: 'Status Updated',
                    text: `${type.replace('_', ' ')} reported for ${activeCandidate.full_name}`,
                    icon: 'success',
                    confirmButtonColor: 'var(--primary)',
                    timer: 2000,
                    customClass: { popup: 'premium-swal' }
                });

                setActiveCandidate(null);
                fetchIncidentHistory();
                setProject(project, true); // Force immediate refresh of running queue
            } catch (err) {
                const pending = JSON.parse(localStorage.getItem('fm_pending_syncs') || '[]');
                localStorage.setItem('fm_pending_syncs', JSON.stringify([...pending, action]));
                setPendingSyncs(pending.length + 1);
                
                Swal.fire({
                    title: 'Offline Mode',
                    text: 'Action cached locally due to connection error.',
                    icon: 'warning',
                    confirmButtonColor: 'var(--primary)',
                    customClass: { popup: 'premium-swal' }
                });
                setActiveCandidate(null);
            }
        }
    };

    const undoLastAction = async (logId) => {
        const log = recentLogs.find(l => l.id === logId);
        if (!log) return;

        const result = await Swal.fire({
            title: 'Undo Incident?',
            text: `Revert the ${log.type.replace('_', ' ')} report for ${log.fullName}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: 'var(--primary)',
            confirmButtonText: 'Yes, Undo',
            customClass: { popup: 'premium-swal' }
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.post(`${CONFIG.API_URL}/api/incidents/undo`, { candidateId: log.candidateId }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const updatedLogs = recentLogs.filter(l => l.id !== logId);
                setRecentLogs(updatedLogs);
                localStorage.setItem('fm_recent_logs', JSON.stringify(updatedLogs));
                addAlert('Action undone successfully.', 'success');
                fetchIncidentHistory();
            } catch (err) {
                addAlert('Failed to undo action.', 'error');
            }
        }
    };

    const runningCandidates = candidates.filter(c => c.current_stage === 'TRACKER' && c.status === 'IN_PROGRESS');

    const filteredHistory = incidentHistory.filter(h => {
        const matchesSearch = h.candidate_id?.full_name?.toLowerCase().includes(historySearch.toLowerCase()) ||
            h.candidate_id?.chest_number?.toLowerCase().includes(historySearch.toLowerCase()) ||
            h.candidate_id?.unique_id?.toLowerCase().includes(historySearch.toLowerCase());

        const matchesType = historyTypeFilter === 'ALL' || h.type === historyTypeFilter;

        return matchesSearch && matchesType;
    });

    const filteredQueue = candidates.filter(c => {
        if (c.current_stage !== 'TRACKER' || c.status !== 'IN_PROGRESS') return false;

        const search = queueSearch.toLowerCase();
        return c.full_name?.toLowerCase().includes(search) ||
            c.chest_number?.toLowerCase().includes(search) ||
            c.unique_id?.toLowerCase().includes(search);
    });

    const handleSelectCandidate = (cand) => {
        setActiveCandidate(cand);
        setActiveTab('incident-scan');
        addAlert(`Selected candidate ${cand.full_name} (#${cand.chest_number})`, 'info');
    };

    const EmptyTableState = ({ title, message, icon }) => (
        <div className="fm-table-empty">
            <div className="fm-table-empty-icon">
                <Icon icon={icon || "ion:document-text-outline"} width="40" />
            </div>
            <h4>{title || "No Records Found"}</h4>
            <p>{message || "There are no entries matching your current view or filter."}</p>
        </div>
    );

    // Internal helpers for Drawer (Replicated from Admin Dashboard)
    const GraphNode = ({ icon, active, label }) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2 }}>
            <div style={{
                width: '36px', height: '36px', borderRadius: '12px',
                background: active ? 'var(--primary)' : '#cbd5e1',
                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '8px', boxShadow: active ? '0 8px 16px rgba(128,0,0,0.15)' : 'none',
                transition: 'all 0.3s'
            }}>
                <Icon icon={icon} width="18" />
            </div>
            <div style={{ fontSize: '10px', fontWeight: 800, color: active ? 'var(--primary)' : 'var(--text-muted)' }}>{label}</div>
        </div>
    );

    const GraphLine = ({ active }) => (
        <div style={{ flex: 1, height: '3px', background: active ? 'var(--primary)' : '#e2e8f0', margin: '0 -4px', position: 'relative', marginTop: '-18px', zIndex: 1 }}>
            {active && (
                <div style={{
                    position: 'absolute', top: 0, left: 0, height: '100%', width: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
                    animation: 'shimmer 1.5s infinite linear'
                }}></div>
            )}
        </div>
    );

    const CandidateDrawer = ({ candidate, onClose }) => (
        <AnimatePresence>
            {candidate && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 3000 }}>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fm-drawer-overlay"
                        onClick={onClose}
                        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)' }}
                    />
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        style={{
                            position: 'fixed', top: 0, right: 0, width: '500px', height: '100%',
                            background: 'white', boxShadow: '-10px 0 50px rgba(0,0,0,0.1)',
                            zIndex: 3100, display: 'flex', flexDirection: 'column'
                        }}
                    >
                        {/* Header - Fixed */}
                        <div style={{ padding: '32px', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{ width: '48px', height: '48px', borderRadius: '16px', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 800 }}>
                                    {candidate.full_name?.charAt(0)}
                                </div>
                                <div>
                                    <h2 style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text-dark)', margin: 0 }}>Candidate Details</h2>
                                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600, margin: 0 }}>{candidate.unique_id}</p>
                                </div>
                            </div>
                            <button onClick={onClose} style={{ background: 'rgba(0,0,0,0.05)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                <Icon icon="ion:close-outline" width="24" />
                            </button>
                        </div>

                        <div className="hide-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '32px 40px' }}>
                            <div style={{ marginBottom: '32px' }}>
                                <h3 style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '8px' }}>{candidate.full_name}</h3>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 10px', background: 'rgba(128,0,0,0.05)', color: 'var(--primary)', borderRadius: '20px', border: '1px solid var(--border-light)' }}>
                                        CURRENT: {candidate.current_stage}
                                    </span>
                                    <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 10px', background: '#f0f9ff', color: '#0369a1', borderRadius: '20px', border: '1px solid #bae6fd' }}>
                                        CHEST: {candidate.chest_number || 'PENDING'}
                                    </span>
                                </div>
                            </div>

                            {/* Graphical Journey Graph */}
                            <div style={{ marginBottom: '40px', padding: '24px', background: '#f8fafc', borderRadius: '20px', border: '1px solid #e2e8f0' }}>
                                <h4 style={{ fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '1px' }}>Mission Pipeline Flow</h4>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 8px' }}>
                                    <GraphNode icon="solar:login-bold-duotone" active={true} label="ENTRY" />
                                    <GraphLine active={['BATCHING', 'TRACKER', 'PHYSICAL_TEST', 'MEDICAL', 'COMPLETED'].includes(candidate.current_stage)} />
                                    <GraphNode icon="solar:users-group-rounded-bold-duotone" active={['BATCHING', 'TRACKER', 'PHYSICAL_TEST', 'MEDICAL', 'COMPLETED'].includes(candidate.current_stage)} label="BATCH" />
                                    <GraphLine active={['TRACKER', 'PHYSICAL_TEST', 'MEDICAL', 'COMPLETED'].includes(candidate.current_stage)} />
                                    <GraphNode icon="solar:clipboard-check-bold-duotone" active={['PHYSICAL_TEST', 'MEDICAL', 'COMPLETED'].includes(candidate.current_stage)} label="PHYSICAL" />
                                    <GraphLine active={['MEDICAL', 'COMPLETED'].includes(candidate.current_stage)} />
                                    <GraphNode icon="solar:medical-kit-bold-duotone" active={['MEDICAL', 'COMPLETED'].includes(candidate.current_stage)} label="MED" />
                                </div>
                            </div>

                            {/* Activity Timeline */}
                            <div style={{ marginBottom: '40px' }}>
                                <h4 style={{ fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '1px' }}>Historical Activity Logs</h4>
                                {(!candidate.activity_log || candidate.activity_log.length === 0) ? (
                                    <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px', fontSize: '14px', border: '2px dashed #f1f5f9', borderRadius: '20px' }}>
                                        No mission logs recorded yet.
                                    </div>
                                ) : (
                                    <div style={{ paddingLeft: '8px' }}>
                                        {[...candidate.activity_log].sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp)).map((log, i) => {
                                            const isNegative = ['REJECTED', 'INJURY', 'INJURED', 'DISQUALIFIED', 'VOLUNTARY_DROP', 'FAIL', 'INCIDENT_REPORTED'].includes(log.action) || log.details?.includes('Result: FAIL');
                                            return (
                                                <div key={i} style={{ display: 'flex', gap: '20px', marginBottom: '32px', position: 'relative' }}>
                                                    <div style={{ 
                                                        width: '28px', height: '28px', borderRadius: '50%', 
                                                        background: isNegative ? '#fee2e2' : '#f0fdf4', 
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
                                                        boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
                                                    }}>
                                                        <Icon icon={isNegative ? "ion:close-circle" : "ion:checkmark-circle"} width="16" color={isNegative ? '#ef4444' : '#10b981'} />
                                                    </div>
                                                    {i < candidate.activity_log.length - 1 && <div style={{ position: 'absolute', left: '13px', top: '28px', bottom: '-32px', width: '2px', background: '#f1f5f9' }}></div>}
                                                    <div>
                                                        <div style={{ fontSize: '14px', fontWeight: 800, color: 'var(--text-dark)' }}>{log.details}</div>
                                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', fontWeight: 600 }}>{new Date(log.timestamp).toLocaleString()} • {log.action}</div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Clearance Status Section */}
                            <div>
                                <h4 style={{ fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '1px' }}>Test Clearance Data</h4>
                                <div style={{ display: 'grid', gap: '16px' }}>
                                    {(candidate.test_results || []).length === 0 ? (
                                        <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No test records available.</div>
                                    ) : (
                                        candidate.test_results.map((test, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 20px', background: test.result === 'PASS' ? '#f0fdf4' : '#fdf2f2', borderRadius: '16px', border: `1px solid ${test.result === 'PASS' ? '#bbf7d0' : '#fecaca'}` }}>
                                                <div>
                                                    <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--text-dark)' }}>{test.stage_name} Result</div>
                                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>VERIFIED ON SYSTEM</div>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{ fontSize: '12px', fontWeight: 900, color: test.result === 'PASS' ? '#166534' : '#991b1b' }}>{test.result}</span>
                                                    <Icon icon={test.result === 'PASS' ? "ion:checkmark-circle" : "ion:close-circle"} width="18" color={test.result === 'PASS' ? '#166534' : '#991b1b'} />
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );

    const renderContent = () => {
        switch (activeTab) {
            case 'incident-scan':
                return (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="fm-view"
                    >
                        {/* Masterpiece Sleek Search Bar */}
                        <div className="fm-search-container">
                            <form onSubmit={handleScan} className="fm-search-bar-sleek">
                                <input
                                    ref={scanInputRef}
                                    type="text"
                                    placeholder="SCAN CHEST NUMBER OR ENTER ID..."
                                    value={scanId}
                                    onChange={(e) => setScanId(e.target.value.toUpperCase())}
                                    className="fm-search-input-sleek"
                                />
                                <button type="submit" className="fm-search-btn-sleek" disabled={loading}>
                                    {loading ? <div className="fm-spinner-small" /> : <Icon icon="ion:arrow-forward" width="20" />}
                                </button>
                            </form>
                        </div>

                        <AnimatePresence mode="wait">
                            {activeCandidate ? (
                                <motion.div
                                    key="candidate"
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    className="fm-candidate-display"
                                >
                                    <button onClick={() => setActiveCandidate(null)} className="fm-btn-close">
                                        <Icon icon="ion:close" width="20" />
                                    </button>
                                    <div className="fm-candidate-header">
                                        <div className="fm-candidate-photo-large">
                                            {activeCandidate.chest_number}
                                        </div>
                                        <div className="fm-candidate-details">
                                            <h2>{activeCandidate.full_name}</h2>
                                            <p>IDENTIFIER: {activeCandidate.unique_id} • BATCH: {activeCandidate.batch_id?.name || 'N/A'}</p>
                                            <div className="fm-status-pill">
                                                <Icon icon="ion:checkmark-circle" style={{ marginRight: '6px' }} />
                                                ACTIVE AT TRACK
                                            </div>
                                        </div>
                                    </div>

                                    <div className="fm-actions-grid">
                                        <button onClick={() => reportIncident('INJURY')} className="fm-action-btn injury">
                                            <Icon icon="ion:medical" width="48" />
                                            <span>MARK INJURY</span>
                                        </button>
                                        <button onClick={() => reportIncident('VOLUNTARY_DROP')} className="fm-action-btn drop">
                                            <Icon icon="ion:exit" width="48" />
                                            <span>VOLUNTARY DROP</span>
                                        </button>
                                        <button onClick={() => reportIncident('DISQUALIFIED')} className="fm-action-btn dq">
                                            <Icon icon="ion:remove-circle" width="48" />
                                            <span>DISQUALIFIED</span>
                                        </button>
                                    </div>

                                </motion.div>
                            ) : (
                                <motion.div
                                    key="empty"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="fm-awaiting-card"
                                >
                                    <div className="fm-placeholder-icon">
                                        <Icon icon="ion:barcode-outline" width="64" />
                                    </div>
                                    <h3>Ready for Scan</h3>
                                    <p>Scan a candidate's chest number or enter their ID to report an incident during the track run.</p>

                                    <div className="fm-stats-shelf">
                                        <div className="fm-stat-item">
                                            <span className="val">{runningCandidates.length}</span>
                                            <span className="lbl">Active Runners</span>
                                        </div>
                                        <div className="fm-stat-item">
                                            <span className="val">{incidentHistory.length}</span>
                                            <span className="lbl">Total Incidents</span>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                );

            case 'running-queue':
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="fm-view"
                    >
                        <div className="fm-history-controls" style={{ marginBottom: '20px' }}>
                            <div className="fm-search-wrap premium">
                                <Icon icon="ion:search-outline" />
                                <input
                                    placeholder="Search by name, chest or ID..."
                                    value={queueSearch}
                                    onChange={(e) => setQueueSearch(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="fm-card">
                            <div className="fm-card-header">
                                <h3>LIVE TRACK RUNNERS ({filteredQueue.length})</h3>
                                <div className="fm-status-pill">{filteredQueue.length} MATCHING</div>
                            </div>
                            <div className="fm-table-wrap">
                                {filteredQueue.length > 0 ? (
                                    <table className="fm-table">
                                        <thead>
                                            <tr>
                                                <th>CHEST</th>
                                                <th>NAME</th>
                                                <th>UID</th>
                                                <th>BATCH</th>
                                                <th style={{ textAlign: 'right' }}>ACTIONS</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredQueue.map(c => (
                                                <tr key={c._id}>
                                                    <td><span className="chest-chip">{c.chest_number}</span></td>
                                                    <td style={{ fontWeight: 700 }}>{c.full_name}</td>
                                                    <td style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{c.unique_id}</td>
                                                    <td><span className="fm-tag">{c.batch_id || 'N/A'}</span></td>
                                                    <td style={{ textAlign: 'right', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                        <button
                                                            onClick={() => setSelectedCandidateForDrawer(c)}
                                                            className="fm-btn-select"
                                                            style={{ background: 'var(--secondary-glass)', color: 'var(--secondary)' }}
                                                        >
                                                            VIEW
                                                        </button>
                                                        <button
                                                            onClick={() => handleSelectCandidate(c)}
                                                            className="fm-btn-select"
                                                        >
                                                            SELECT
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <EmptyTableState
                                        title="No Runners Found"
                                        message={queueSearch ? "No active runners match your search criteria." : "There are no candidates currently active in the track session."}
                                        icon="ion:people-outline"
                                    />
                                )}
                            </div>
                        </div>
                    </motion.div>
                );

            case 'history':
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="fm-view"
                    >
                        <div className="fm-history-top-controls">
                            <div className="fm-search-wrap premium" style={{ flex: 1 }}>
                                <Icon icon="ion:search-outline" />
                                <input placeholder="Search candidate..." value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} />
                            </div>
                            <div className="fm-filter-wrap" style={{ width: '200px', padding: 0 }}>
                                <select value={historyTypeFilter} onChange={(e) => setHistoryTypeFilter(e.target.value)} className="fm-type-select" style={{ width: '100%', paddingLeft: '12px' }}>
                                    <option value="ALL">ALL TYPES</option>
                                    <option value="INJURED">INJURIES</option>
                                    <option value="VOLUNTARY_DROP">DROPS</option>
                                    <option value="DISQUALIFIED">DQ</option>
                                </select>
                            </div>
                        </div>

                        <div className="fm-card">
                            <div className="fm-card-header">
                                <h3>INCIDENT HISTORY ({filteredHistory.length})</h3>
                                <div className="fm-status-pill" style={{ marginLeft: 'auto' }}>{filteredHistory.length} ENTRIES</div>
                            </div>

                            <div className="fm-table-wrap">
                                {filteredHistory.length > 0 ? (
                                    <table className="fm-table fm-history-table">
                                        <thead>
                                            <tr>
                                                <th>CANDIDATE</th>
                                                <th>TYPE</th>
                                                <th>TIMESTAMP</th>
                                                <th>STATUS</th>
                                                <th style={{ textAlign: 'right' }}>DETAILS</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredHistory.map(h => (
                                                <tr key={h._id}>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                            <span className="chest-chip small">{h.candidate_id?.chest_number}</span>
                                                            <span style={{ fontWeight: 700 }}>{h.candidate_id?.full_name}</span>
                                                        </div>
                                                    </td>
                                                    <td><span className={`fm-type-badge ${h.type.toLowerCase()}`}>{h.type.replace('_', ' ')}</span></td>
                                                    <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{new Date(h.timestamp).toLocaleString()}</td>
                                                    <td><span className={`fm-sync-badge ${h.status === 'ACTIVE' ? 'active' : 'reversed'}`}>{h.status}</span></td>
                                                    <td style={{ textAlign: 'right' }}><button onClick={() => setSelectedIncident(h)} className="fm-btn-select">VIEW LOG</button></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <EmptyTableState
                                        title="No History Found"
                                        message="No incident logs match your current search or filters."
                                        icon="ion:time-outline"
                                    />
                                )}
                            </div>
                        </div>

                        <AnimatePresence>
                            {selectedIncident && (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fm-modal-overlay" onClick={() => setSelectedIncident(null)}>
                                    <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="fm-modal-card" onClick={e => e.stopPropagation()}>
                                        <div className="fm-modal-header">
                                            <h3>INCIDENT DOSSIER</h3>
                                            <button onClick={() => setSelectedIncident(null)}><Icon icon="ion:close" /></button>
                                        </div>
                                        <div className="fm-detail-grid">
                                            <div className="detail-item"><label>Candidate</label><p>{selectedIncident.candidate_id?.full_name}</p></div>
                                            <div className="detail-item"><label>Chest No.</label><p>#{selectedIncident.candidate_id?.chest_number}</p></div>
                                            <div className="detail-item"><label>Type</label><p>{selectedIncident.type}</p></div>
                                            <div className="detail-item"><label>Timestamp</label><p>{new Date(selectedIncident.timestamp).toLocaleString()}</p></div>
                                        </div>
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                );

            case 'profile':
                return <div className="fm-view"><Profile user={JSON.parse(localStorage.getItem('user'))} /></div>;

            default: return null;
        }
    };

    return (
        <div className="fm-main-layout" style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-main)' }}>
            <FieldMonitorSidebar activeTab={activeTab} setActiveTab={setActiveTab} onLogout={onLogout} />
            <main className="fm-content-wrapper" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <FieldMonitorNavbar
                    tabTitle={activeTab.replace('-', ' ').toUpperCase()}
                    setActiveTab={setActiveTab}
                    pendingSyncs={pendingSyncs}
                    onSync={() => {
                        if (pendingSyncs > 0) {
                            processPendingSyncs();
                        } else {
                            addAlert('System is already synchronized.', 'info');
                        }
                    }}
                />
                <div className="fm-main-content">
                    {!project ? (
                        <div className="fm-empty-state">
                            <Icon icon="ion:warning-outline" width="80" />
                            <h2>DRIVE NOT SELECTED</h2>
                            <p>Please select an active project drive to begin monitor.</p>
                        </div>
                    ) : renderContent()}
                </div>
            </main>

            {/* Admin-Style Simulation Engine */}
            <button className="fm-sim-toggle" onClick={() => setIsSimOpen(!isSimOpen)}>
                <Icon icon={isSimOpen ? "ion:close" : "ion:bug"} width="28" />
            </button>

            <AnimatePresence>
                {isSimOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className="fm-sim-popup hide-scrollbar"
                    >
                        <div className="fm-sim-header">
                            <Icon icon="ion:bug" width="20" />
                            <h3>SIMULATION ENGINE</h3>
                        </div>
                        <div className="fm-sim-group">
                            <div className="fm-sim-group-title">Entry Controls</div>
                            <button className="fm-sim-btn" onClick={() => simulateArrivals(10)}>Arrivals +10</button>
                            <button className="fm-sim-btn" onClick={() => simulateArrivals(50)}>Arrivals +50</button>
                        </div>
                        <div className="fm-sim-group">
                            <div className="fm-sim-group-title">Force Incident Simulations</div>
                            <button className="fm-sim-btn injury" onClick={() => {
                                if (!activeCandidate) {
                                    Swal.fire({
                                        title: 'Simulation Blocked',
                                        text: 'Select the candidate first for simulation.',
                                        icon: 'warning',
                                        confirmButtonColor: 'var(--primary)',
                                        customClass: { popup: 'premium-swal' }
                                    });
                                } else {
                                    reportIncident('INJURY');
                                }
                            }}>Force Injury</button>
                            <button className="fm-sim-btn dq" onClick={() => {
                                if (!activeCandidate) {
                                    Swal.fire({
                                        title: 'Simulation Blocked',
                                        text: 'Select the candidate first for simulation.',
                                        icon: 'warning',
                                        confirmButtonColor: 'var(--primary)',
                                        customClass: { popup: 'premium-swal' }
                                    });
                                } else {
                                    reportIncident('DISQUALIFIED');
                                }
                            }}>Force Disqualification</button>
                            <button className="fm-sim-btn drop" onClick={() => {
                                if (!activeCandidate) {
                                    Swal.fire({
                                        title: 'Simulation Blocked',
                                        text: 'Select the candidate first for simulation.',
                                        icon: 'warning',
                                        confirmButtonColor: 'var(--primary)',
                                        customClass: { popup: 'premium-swal' }
                                    });
                                } else {
                                    reportIncident('VOLUNTARY_DROP');
                                }
                            }}>Force Voluntary Drop</button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Candidate Detail Drawer */}
            <AnimatePresence>
                {selectedCandidateForDrawer && (
                    <CandidateDrawer
                        candidate={candidates.find(c => c._id === selectedCandidateForDrawer._id) || selectedCandidateForDrawer}
                        onClose={() => setSelectedCandidateForDrawer(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

const FieldMonitorDashboard = (props) => (
    <SimulationProvider>
        <FieldMonitorContent {...props} />
    </SimulationProvider>
);

export default FieldMonitorDashboard;
